// Sprint 25, story-134. Wire types for SubcontractorResolver's three queries.

export enum SubcontractorHealth {
  NONE = 'NONE',
  COMPLETE = 'COMPLETE',
  NOT_STARTED = 'NOT_STARTED',
  BEHIND = 'BEHIND',
  ON_TRACK = 'ON_TRACK',
}

export interface SubcontractorRosterEntry {
  organizationId: string;
  name: string;
  projectsCount: number;
  activeTasksCount: number;
  completeTasksCount: number;
  overdueCount: number;
  awaitingApprovalCount: number;
  openIncidentsCount: number;
  attention: number;
}

export interface SubcontractorHeader {
  since?: string | null;
  projectsCount: number;
  tasksTotal: number;
  tasksActive: number;
  tasksComplete: number;
  tasksCancelled: number;
  agreedPriceTotal?: number | null;
  tasksWithoutPriceCount: number;
}

export interface SubcontractorTotals {
  health: SubcontractorHealth;
  completion: number;
  overdueCount: number;
  completedLateCount: number;
  avgDaysLate?: number | null;
  earned: number;
  ofAgreedPct?: number | null;
  atRisk: number;
  dueThisWeekCount: number;
}

export interface SubcontractorInventoryLine {
  name: string;
  quantity?: number | null;
  unit?: string | null;
  estimatedHours?: number | null;
  pricePerUnit?: number | null;
  isMine: boolean;
}

export interface SubcontractorTaskOrigin {
  type: 'BID' | 'DIRECT';
  amount?: number | null;
}

export interface SubcontractorTask {
  taskId: string;
  name: string;
  status: string;
  startTime?: string | null;
  endTime?: string | null;
  budget?: number | null;
  daysLate?: number | null;
  daysLeft?: number | null;
  completedAt?: string | null;
  countable: boolean;
  origin: SubcontractorTaskOrigin;
  materials: SubcontractorInventoryLine[];
  equipment: SubcontractorInventoryLine[];
}

export interface SubcontractorProject {
  projectId: string;
  projectName: string;
  health: SubcontractorHealth;
  completion: number;
  completeCount: number;
  countableCount: number;
  dueThisWeekCount: number;
  agreed: number;
  earned: number;
  remaining: number;
  atRisk: number;
  tasks: SubcontractorTask[];
}

export interface SubcontractorExceptionRow {
  taskId: string;
  taskName: string;
  projectId: string;
  projectName: string;
  reason?: string | null;
  incidentId?: string | null;
  date?: string | null;
}

export interface SubcontractorExceptions {
  awaitingApproval: SubcontractorExceptionRow[];
  openIncidents: SubcontractorExceptionRow[];
  overdue: SubcontractorExceptionRow[];
}

export interface SubcontractorMaterialsEquipmentRollup {
  materialsCount: number;
  equipmentCount: number;
  priceTotal: number;
}

export interface SubcontractorOverview {
  subOrganizationId: string;
  subOrganizationName: string;
  header: SubcontractorHeader;
  totals: SubcontractorTotals;
  projects: SubcontractorProject[];
  exceptions: SubcontractorExceptions;
  materialsEquipmentRollup: SubcontractorMaterialsEquipmentRollup;
}

export type SubcontractorBidOutcome = 'ACCEPTED' | 'DECLINED' | 'COUNTERED' | 'OPEN' | 'CANCELLED';

export interface SubcontractorBid {
  budgetRequestId: string;
  taskId: string;
  taskName: string;
  projectId?: string | null;
  projectName?: string | null;
  sentAt: string;
  maxBudgetAmount?: number | null;
  proposedAmount?: number | null;
  outcome: SubcontractorBidOutcome;
  agreedAmount?: number | null;
}

export interface getSubcontractorRosterRequest {
  getSubcontractorRoster: SubcontractorRosterEntry[];
}

export interface getSubcontractorOverviewRequest {
  getSubcontractorOverview: SubcontractorOverview;
}

export interface getSubcontractorBidsRequest {
  getSubcontractorBids: SubcontractorBid[];
}
